import { useState } from 'react';
import type { VehicleModelType } from '../../../data/vehicleModelType';
import { OccurrenceStepEnum } from '../../../enums/occurrenceStep';
import {
    type DateRange,
    type PeriodPreset,
    getPeriodRange,
    matchPeriodPreset,
    sameList,
    sameRange,
    toRangeFromInputs,
    toRangeInputs,
} from '../trackingUtils';
import type { OccurrenceState, TrackingStatus } from '../types';
import type { SavedView } from '../ui/TrackingChips';
import { type AlertFilterValue, EMPTY_MORE_FILTERS, type MoreFiltersValue } from '../ui/TrackingMoreFilters';

export const CLEAR_RANGE: DateRange = { start: undefined, end: undefined };

interface ITrackingFiltersInput {
    occurrences: OccurrenceState;
    modelsByType: Map<VehicleModelType, Set<string>>;
}

const useTrackingFilters = ({ occurrences, modelsByType }: ITrackingFiltersInput) => {
    const {
        searchByChassis,
        setChassisListFilter,
        setCriticalityFilter,
        criticalityFilter,
        typeFilter,
        setTypeFilter,
        setStartDateFilter,
        startDateFilter,
        setUpdateDateFilter,
        updateDateFilter,
        setIsAlertFilter,
        isAlertFilter,
        finishedFilter,
        setFinishedFilter,
        dealershipFilter,
        setDealershipFilter,
        regionalFilter,
        setRegionalFilter,
        cellFilter,
        setCellFilter,
        modelFilter,
        setModelFilter,
        cpvNameFilter,
        setCpvNameFilter,
        stepFilter,
        setStepFilter,
        stepTypeCurrentFilter,
        setStepTypeCurrentFilter,
        searchFieldName,
        searchFieldValue,
        rawCurrentOccurrenceWithTimeSpentList,
        finalizedOccurrenceWithTimeSpentList,
    } = occurrences;

    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [more, setMore] = useState<MoreFiltersValue>(EMPTY_MORE_FILTERS);
    const [currentStageMode, setCurrentStageMode] = useState(false);

    const finished = finishedFilter.includes('true') && !finishedFilter.includes('false');
    const status: TrackingStatus = currentStageMode ? 'current' : finished ? 'finished' : 'open';
    const activeStage = currentStageMode ? stepTypeCurrentFilter : stepFilter;
    const criticality = criticalityFilter && criticalityFilter !== 'undefined' ? criticalityFilter : '';
    const alertOnly = isAlertFilter === 'true';
    const alertValue: AlertFilterValue = isAlertFilter === 'true' || isAlertFilter === 'false' ? isAlertFilter : 'all';
    const period = matchPeriodPreset(startDateFilter);

    const openRange = toRangeInputs(startDateFilter);
    const updateRange = toRangeInputs(updateDateFilter);
    const drawerValue: MoreFiltersValue = {
        ...more,
        alert: alertValue,
        regionals: regionalFilter,
        cells: cellFilter,
        cpv: cpvNameFilter ?? '',
        types: typeFilter,
        passedStep: stepFilter ?? '',
        openFrom: openRange.from,
        openTo: openRange.to,
        updateFrom: updateRange.from,
        updateTo: updateRange.to,
    };

    const resetStage = () => {
        if (stepTypeCurrentFilter) {
            setStepTypeCurrentFilter(null);
        }
    };

    const withStageReset =
        <A extends unknown[]>(action: (...args: A) => void) =>
        (...args: A) => {
            resetStage();
            action(...args);
        };

    const applyMoreFilters = (next: MoreFiltersValue) => {
        const changes: (() => void)[] = [];
        if (next.alert !== alertValue) {
            changes.push(() => setIsAlertFilter(next.alert === 'all' ? 'undefined' : next.alert));
        }
        if (!sameList(next.regionals, regionalFilter)) {
            changes.push(() => setRegionalFilter(next.regionals));
        }
        if (!sameList(next.cells, cellFilter)) {
            changes.push(() => setCellFilter(next.cells));
        }
        if ((next.cpv || undefined) !== (cpvNameFilter || undefined)) {
            changes.push(() => setCpvNameFilter(next.cpv || undefined));
        }
        const dealershipIds = next.dealerships.map(dealership => dealership.id);
        if (!sameList(dealershipIds, dealershipFilter)) {
            changes.push(() => setDealershipFilter(dealershipIds));
        }
        const models = [
            ...new Set([...next.models, ...next.modelTypes.flatMap(type => [...(modelsByType.get(type) ?? [])])]),
        ];
        if (!sameList(models, modelFilter)) {
            changes.push(() => setModelFilter(models));
        }
        if (!sameList(next.types, typeFilter)) {
            changes.push(() => setTypeFilter(next.types));
        }
        if (!sameList(next.clients, more.clients)) {
            const selected = new Set(next.clients);
            const chassis = [...rawCurrentOccurrenceWithTimeSpentList, ...finalizedOccurrenceWithTimeSpentList]
                .filter(item => selected.has(item.customer))
                .map(item => item.chassis);
            changes.push(() => setChassisListFilter(next.clients.length ? [...new Set(chassis)] : []));
        }
        const nextOpenRange = toRangeFromInputs(next.openFrom, next.openTo);
        if (!sameRange(nextOpenRange, startDateFilter)) {
            changes.push(() => setStartDateFilter(nextOpenRange ?? CLEAR_RANGE));
        }
        const nextUpdateRange = toRangeFromInputs(next.updateFrom, next.updateTo);
        if (!sameRange(nextUpdateRange, updateDateFilter)) {
            changes.push(() => setUpdateDateFilter(nextUpdateRange ?? CLEAR_RANGE));
        }
        if ((next.passedStep || null) !== stepFilter) {
            changes.push(() => setStepFilter((next.passedStep as OccurrenceStepEnum) || null));
        }
        if (changes.length) {
            resetStage();
        }
        for (const change of changes) {
            change();
        }
        setMore({ ...next });
        setShowMoreFilters(false);
    };

    const resetAll = () => {
        if (searchFieldValue) {
            searchByChassis('', searchFieldName);
        }
        if (criticality) {
            setCriticalityFilter('undefined');
        }
        resetStage();
        applyMoreFilters(EMPTY_MORE_FILTERS);
    };

    const applySavedView = (saved: SavedView) => {
        resetAll();
        if (saved === 'emergency') {
            setCriticalityFilter('5');
        }
        if (saved === 'alert') {
            setIsAlertFilter('true');
        }
        if (saved === 'parts') {
            setCurrentStageMode(true);
            if (finished) {
                setFinishedFilter(['false']);
            }
            setStepTypeCurrentFilter(OccurrenceStepEnum.PARTS);
        }
    };

    const changeStatus = (next: TrackingStatus) => {
        setCurrentStageMode(next === 'current');
        resetStage();
        if (stepFilter) {
            setStepFilter(null);
        }
        const nextFinished = next === 'finished';
        if (nextFinished !== finished) {
            setFinishedFilter(nextFinished ? ['true'] : ['false']);
        }
    };

    const toggleStage = (step: string) => {
        const value = step as OccurrenceStepEnum;
        if (currentStageMode) {
            setStepTypeCurrentFilter(stepTypeCurrentFilter === value ? null : value);
            return;
        }
        setStepFilter(stepFilter === value ? null : value);
    };

    const toolbarHandlers = {
        onSearch: withStageReset((term: string, field: string) => searchByChassis(term, field)),
        onCriticalityChange: withStageReset((value: string) => setCriticalityFilter(value || 'undefined')),
        onAlertToggle: withStageReset(() => setIsAlertFilter(alertOnly ? 'undefined' : 'true')),
        onPeriodChange: withStageReset((preset: PeriodPreset) =>
            setStartDateFilter(getPeriodRange(preset) ?? CLEAR_RANGE)
        ),
    };

    return {
        finished,
        status,
        activeStage,
        criticality,
        alertOnly,
        period,
        currentStageMode,
        more,
        drawerValue,
        showMoreFilters,
        setShowMoreFilters,
        withStageReset,
        applyMoreFilters,
        resetAll,
        applySavedView,
        changeStatus,
        toggleStage,
        toolbarHandlers,
    };
};

export type TrackingFilters = ReturnType<typeof useTrackingFilters>;

export default useTrackingFilters;
